import React from 'react'
import { Card } from 'antd'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ResponsiveBullet } from '@nivo/bullet'
import CommonLayout from '../../../../../layouts/Common'
import Chart from 'react-apexcharts';
import { BulletChartWrapper, Container, FlexContainer, HRVHeader, StyledCard } from './style'
import { StyledButton } from '../../../../../components/Common/Button/style'
import { formatDateWithoutTime } from '../../../../../helpers'
import { _COMMON_BULLET_CHART_PROPS } from '../../../../../constants'

type Props = {}

const HMAResult = (props: Props) => {
  const { hmaId } = useParams();
  const location = useLocation();
  const [payload, setPayload] = React.useState(location.state?.payload);
  const [client] = useSelector((state: any) => [state.professionals.selectedClient]);

  React.useEffect(() => {
    if (!payload && hmaId) {
      const stored = sessionStorage.getItem(`analysis_payload_${hmaId}`);
      if (stored) {
        setPayload(JSON.parse(stored));
      }
    }
  }, [hmaId, payload]);
  const navigate = useNavigate();
  const [isViewerVisible, setIsViewerVisible] = React.useState(true);
  const [isEnlarged, setIsEnlarged] = React.useState(false);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [isBlobLoading, setIsBlobLoading] = React.useState(false);

  React.useEffect(() => {
    if (payload?.attachmentUrl) {
      setIsBlobLoading(true);
      fetch(payload.attachmentUrl)
        .then(res => res.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
          setIsBlobLoading(false);
        })
        .catch(err => {
          console.error('Failed to proxy PDF blob:', err);
          setIsBlobLoading(false);
        });
    }
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [payload?.attachmentUrl]);

  const formatTitle = (
    name: any,
    shortName: any,
    measure: any
  ) => {
    return (
      <text dy="-29">
        <tspan x="0" textAnchor="middle" style={{ fontSize: '18px' }}>{shortName}</tspan>
        <tspan x="0" textAnchor="middle" dy="1.4em" style={{ fontSize: '18px', fontWeight: 800, fill: '#0066D2' }}>{measure}</tspan>
        <tspan x="0" textAnchor="middle" dy="1.4em" style={{ fontSize: '14px' }}>{name}</tspan>
      </text>
    )
  }

  const MINERAL_MAP = [
    { label: 'CALCIUM', short: 'Ca', key: 'calcium' },
    { label: 'MAGNESIUM', short: 'Mg', key: 'magnesium' },
    { label: 'SODIUM', short: 'Na', key: 'sodium' },
    { label: 'POTASSIUM', short: 'K', key: 'potassium' },
    { label: 'IRON', short: 'Fe', key: 'iron' },
    { label: 'COPPER', short: 'Cu', key: 'copper' },
    { label: 'MANGANESE', short: 'Mn', key: 'manganese' },
    { label: 'ZINC', short: 'Zn', key: 'zinc' },
    { label: 'CHROMIUM', short: 'Cr', key: 'chromium' },
    { label: 'SELENIUM', short: 'Se', key: 'selenium' },
    { label: 'PHOSPHORUS', short: 'P', key: 'phosphorus' },
  ];

  const data = MINERAL_MAP.map((m, index) => {
    const value = payload ? (payload[m.key] || 0) : 0;
    return {
      "id": index + 1,
      "ranges": [0, 31, 65, 120],
      "measures": [value],
      "markers": [40],
      title: formatTitle(m.label, m.short, value)
    };
  });

  const importantMineralSeries = [44, 55, 13, 33];
  const importantMineral = {
    chart: {
      id: 'donut',
    },
    legend: {
      show: true
    },
    series: importantMineralSeries,
    labels: ['Apple', 'Mango', 'Orange', 'Watermelon'],
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 90,
        customScale: 1,
        donut: {
          size: '50%'
        },
        labels: {
          show: false
        }
      },
      donut: {
        labels: {
          show: false
        }
      }
    },
  };

  return (
    <CommonLayout title="HMA Results" width="100%">
      <div style={{ width: '100%', padding: '0 20px' }}>
        <HRVHeader>
          <Card style={{ width: '100%' }}>
            <div className="hrv-details">
              <div>
                <p>Report number:</p>
                <p>{payload?.reportNumber}</p>
              </div>
              <div>
                <p>Date of analysis:</p>
                <p>{payload?.dateOfAnalysis && formatDateWithoutTime(payload.dateOfAnalysis)}</p>
              </div>
              <div>
                <p>Client</p>
                <p>{payload?.client ? payload.client : `${client?.firstName} ${client?.familyName}`}</p>
              </div>
              <div>
                <p>Client ID</p>
                <p>{payload?.clientGid || client?.gid}</p>
              </div>
            </div>
          </Card>
        </HRVHeader>

        {payload?.attachmentUrl && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '1rem', justifyContent: 'flex-start' }}>
              <StyledButton 
                onClick={() => setIsViewerVisible(!isViewerVisible)}
                style={{ background: isViewerVisible ? '#E2E8F0' : '#0066D2', color: isViewerVisible ? '#4A5568' : '#fff', border: 'none', height: '36px', padding: '0 16px', borderRadius: '6px' }}
              >
                {isViewerVisible ? 'Hide Attachment' : 'Show Attachment'}
              </StyledButton>
              <StyledButton 
                onClick={() => {
                  const downloadUrl = blobUrl || payload.attachmentUrl;
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = `HMA_Report_${payload.reportNumber || payload.id}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{ background: '#718096', color: '#fff', border: 'none', height: '36px', padding: '0 16px', borderRadius: '6px' }}
              >
                Download PDF
              </StyledButton>
              <StyledButton 
                onClick={() => window.open(payload.attachmentUrl, '_blank')}
                style={{ background: '#0066D2', color: '#fff', border: 'none', height: '36px', padding: '0 16px', borderRadius: '6px' }}
              >
                Open in New Tab
              </StyledButton>
              {isViewerVisible && (
                <StyledButton 
                  onClick={() => setIsEnlarged(!isEnlarged)}
                  style={{ background: '#E2E8F0', color: '#4A5568', border: 'none', height: '36px', padding: '0 16px', borderRadius: '6px' }}
                >
                  {isEnlarged ? 'Original Size' : 'Enlarge View'}
                </StyledButton>
              )}
            </div>

            {isViewerVisible && (
              <Card>
                <p style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '1.5rem' }}>Lifestyle Assessment</p>
                <div style={{ position: 'relative', width: '100%', height: isEnlarged ? '1200px' : '650px', border: '1px solid #edf2f7', borderRadius: '8px', overflow: 'hidden', background: '#f8fafc', transition: 'height 0.3s ease' }}>
                  {isBlobLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <p>Loading report viewer...</p>
                    </div>
                  ) : (
                    <iframe
                      src={blobUrl ? `${blobUrl}#toolbar=0` : `${payload.attachmentUrl}#toolbar=0`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      title="HMA Report"
                    />
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        <StyledCard>
          <Container>
            <p className="">Nutrients and Minerals</p>
            <BulletChartWrapper>
              <ResponsiveBullet
                data={data}
                layout="vertical"
                titleAlign="start"
                axisPosition="before"
                {..._COMMON_BULLET_CHART_PROPS}
              />
            </BulletChartWrapper>
          </Container>
          <Container style={{ marginTop: 80 }}>
            <p className="">Important mineral proportions</p>
            <FlexContainer>
              {[...Array(6)].map((val: any, index: number) => (
                <div key={index}>
                  <Chart
                    width={200}
                    options={importantMineral}
                    series={importantMineralSeries}
                    type="donut"
                    hideSeries
                  />
                  <div className="chart-title">
                    <p>Na/K: <span>2</span></p>
                  </div>
                  <div>
                    <p>Ideal: <span>2.5-5</span></p>
                    <p>Optimal Ratio: <span>2.5</span></p>
                    <p>% Optimal Ratio: <span>80</span></p>
                  </div>
                </div>
              ))}
            </FlexContainer>
            <div className='summary'>
              <div>
                <p>* Your body has a fast mixed oxidation rate</p>
                <p>* Temporarily fast oxidizer</p>
                <p>* Type of stress: Exhaustion phase</p>
              </div>
            </div>
          </Container>
        </StyledCard>
      </div>
    </CommonLayout>
  )
}

export default HMAResult;